import { Navigate } from 'react-router-dom'
import { useTenant, useAuth } from '@/contexts'
import { Loader2 } from 'lucide-react'
import { normalizeSlug } from '@/lib/slugs'

/**
 * Smart redirect component for the root path (/).
 *
 * Redirect logic:
 * 1. Single-location org → /{locationSlug}/dashboard
 * 2. Multi-location with user default → /{defaultLocationSlug}/dashboard
 * 3. Multi-location without default → /dashboard (portfolio view)
 *
 * This provides a good UX where single-location users go directly to
 * their location's dashboard, while multi-location users see the
 * portfolio overview first.
 */
export function RootRedirect() {
  const { user } = useAuth()
  const { locations, isLoading } = useTenant()

  // Show loading while data is being fetched
  if (isLoading || !user) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // No locations - go to portfolio dashboard
  if (!locations || locations.length === 0) {
    return <Navigate to="/dashboard" replace />
  }

  // Single-location org - go directly to location dashboard
  if (locations.length === 1) {
    const slug = normalizeSlug(locations[0].location_code)
    return <Navigate to={`/${slug}/dashboard`} replace />
  }

  // Multi-location org - check for user's default location preference
  // For now, we don't have user preferences stored, so go to portfolio
  // TODO: Add user preferences for default location
  // const defaultLocationId = user.preferences?.defaultLocationId
  // if (defaultLocationId) {
  //   const defaultLocation = locations.find(l => l.id === defaultLocationId)
  //   if (defaultLocation) {
  //     const slug = normalizeSlug(defaultLocation.location_code)
  //     return <Navigate to={`/${slug}/dashboard`} replace />
  //   }
  // }

  // Multi-location without default - show portfolio dashboard
  return <Navigate to="/dashboard" replace />
}
